// First-touch marketing attribution.
//
// We are identified-events-only (no anonymous capture), so by the time a
// visitor signs up the original landing context is gone. This module stores the
// acquisition context first-touch in localStorage so the signup flow can attach
// it later.
//
// Discreet by design: marketing links are 4casters.io/r/<code>, where <code> is
// an OPAQUE code the marketer invents (e.g. "7j54"). The code's meaning — which
// channel/campaign it represents — lives in a private marketing sheet, never in
// the URL. So we capture the raw code only; we do NOT decode source/campaign
// from it. In PostHog you see the code and look up its meaning in the sheet.
//
// We also opportunistically record standard UTM params and the external
// referrer host if present (handy when marketing links happen to carry them),
// but the opaque /r/ code is the primary, intended mechanism.
//
// First-touch wins: once stored we never overwrite, so the first link a visitor
// arrived through gets the credit even if they return via another.

const STORAGE_KEY = 'mkt_src_v1';

function persistFirstTouch(attribution) {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return; // first-touch already set
    localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // localStorage unavailable (private mode/quota) — attribution is a
    // nice-to-have, never break over it.
  }
}

function safeReferrerHost() {
  try {
    if (!document.referrer) return null;
    const url = new URL(document.referrer);
    if (url.host === window.location.host) return null; // ignore internal nav
    return url.host;
  } catch {
    return null;
  }
}

// Search-engine referrer hosts → engine name. Matched against the bare host,
// so regional and subdomain variants (www.google.co.uk, r.search.yahoo.com)
// collapse onto one engine name instead of fragmenting analytics.
const SEARCH_ENGINES = [
  ['google', /(^|\.)google\.[a-z]+(\.[a-z]+)?$/],
  ['bing', /(^|\.)bing\.com$/],
  ['duckduckgo', /(^|\.)duckduckgo\.com$/],
  ['yahoo', /(^|\.)yahoo\.(com|co\.[a-z]{2}|[a-z]{2})$/],
  ['brave', /(^|\.)search\.brave\.com$/],
  ['ecosia', /(^|\.)ecosia\.org$/],
  ['yandex', /(^|\.)yandex\.(com|ru|[a-z]{2})$/],
  ['baidu', /(^|\.)baidu\.com$/],
  ['startpage', /(^|\.)startpage\.com$/],
];

// Social platforms we classify separately from generic referrals. Suffix-matched
// so subdomains (m.facebook.com, out.reddit.com) count too.
const SOCIAL_HOSTS = [
  'facebook.com',
  'instagram.com',
  'twitter.com',
  'x.com',
  't.co',
  'reddit.com',
  'tiktok.com',
  'linkedin.com',
  'youtube.com',
  'threads.net',
  'discord.com',
];

// Classify an external referrer host into a coarse acquisition channel.
// Null/empty host means the visitor arrived with no external referrer (typed
// URL, bookmark, most apps) → 'direct'. Anything external that isn't a search
// engine or social platform is a plain 'referral'.
//
// NOTE: the PRO-118 SEO branch carries its own internal referrerInfo() inside
// PostHogUtils; when it merges it should consolidate onto this exported helper
// so both report the same channel taxonomy.
export function classifyReferrer(referrerHost) {
  if (!referrerHost) return { referrer_source: 'direct', search_engine: null };
  const host = String(referrerHost).toLowerCase();
  for (const [engine, pattern] of SEARCH_ENGINES) {
    if (pattern.test(host)) {
      return { referrer_source: 'organic', search_engine: engine };
    }
  }
  if (SOCIAL_HOSTS.some((d) => host === d || host.endsWith(`.${d}`))) {
    return { referrer_source: 'social', search_engine: null };
  }
  return { referrer_source: 'referral', search_engine: null };
}

// Referrer context for analytics events: the external referrer host (or null)
// plus its channel classification, computed from the current document.
export function referrerContext() {
  const referrerHost = safeReferrerHost();
  return { referrer_host: referrerHost, ...classifyReferrer(referrerHost) };
}

// Read UTM params from the current URL (or all-null if absent/unparseable).
// Marketing links can carry UTMs alongside an /r/ code, so both paths use this.
function readUtmParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || null,
      utm_medium: params.get('utm_medium') || null,
      utm_campaign: params.get('utm_campaign') || null,
    };
  } catch {
    return { utm_source: null, utm_medium: null, utm_campaign: null };
  }
}

export const MarketingSource = {
  // Record an opaque marketing code from a /r/<code> link. Called by the router
  // redirect. First-touch only.
  recordCode(rawCode) {
    const code = typeof rawCode === 'string' ? rawCode.trim() : '';
    // Codes are short opaque tokens (e.g. "7j54"). Reject empty, oversized, or
    // non [A-Za-z0-9_-] input so a junk/huge URL can't pollute attribution or
    // get stamped onto a person profile.
    if (!code || code.length > 32 || !/^[A-Za-z0-9_-]+$/.test(code)) return;
    // Capture any UTM params on the /r/ link too, before the redirect to / drops
    // them from the URL.
    persistFirstTouch({
      marketing_code: code,
      ...readUtmParams(),
      referrer_host: safeReferrerHost(),
    });
  },

  // Call once on app boot. Captures UTM params / external referrer as a
  // fallback when there's no /r/ code (e.g. a UTM-tagged ad link). Cheap and
  // safe to call on every load; first-touch guarded.
  captureOnLoad() {
    // Skip on /r/ landings — the route handler records the opaque code, and we
    // don't want this UTM/referrer fallback to claim first-touch before it.
    try {
      if (window.location.pathname.startsWith('/r/')) return;
    } catch {
      return;
    }
    const utm = readUtmParams();
    const referrerHost = safeReferrerHost();

    if (!utm.utm_source && !utm.utm_medium && !utm.utm_campaign && !referrerHost) {
      return;
    }

    persistFirstTouch({
      marketing_code: null,
      ...utm,
      referrer_host: referrerHost,
    });
  },

  // Read the stored attribution (or null). Used by the signup flow.
  get() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },

  // Drop the stored attribution. Called once it's been stamped onto a new
  // account's profile, so a later refresh/getMe can't re-apply signup
  // attribution to a returning user.
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },
};

export default MarketingSource;
